import React, { createContext, useContext, useState } from "react";
import { useEffect, useMemo } from "react";
import useSyncedLocalStorage from "../hooks/useSyncedLocalStorage";
import { adaptive3 } from "./themes/Adaptive3";
import { admin, adminDark } from "./themes/Administrator";
import { av } from "./themes/AsplanViak";
import { kv } from "./themes/Kystverket";
import { tkrog } from "./themes/TemakartRogaland";
import { AdaptiveUiThemeStyle } from "./utils/AdaptiveUiThemeStyle";
import "./style/global.scss";
export const AdaptiveUiContext = createContext({
    scope: "aui-root aui-theme-admin",
    themeMode: "light",
    setThemeMode: () => {
        return;
    },
});
/**
 * Get the AdaptiveUi context.
 */
export function useAdaptiveUi() {
    return useContext(AdaptiveUiContext);
}
/**
 * Component handling Adaptive UI specific contexts, styling, etc. Apps must
 * wrap their app in this component to use the provided styling and certain
 * features.
 */
export default function AdaptiveUi({ theme = "adaptive", mode, children, local = false, }) {
    const [prefersDark, setPrefersDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
    const [userMode, setUserMode] = useSyncedLocalStorage("aui-theme-mode");
    const documentMode = document.documentElement.getAttribute("data-theme");
    const forcedMode = userMode === "light"
        ? "light"
        : userMode === "dark"
            ? "dark"
            : mode !== null && mode !== void 0 ? mode : (documentMode === "dark"
                ? "dark"
                : documentMode === "light"
                    ? "light"
                    : prefersDark
                        ? "dark"
                        : "light");
    useEffect(() => {
        window
            .matchMedia("(prefers-color-scheme: dark)")
            .addEventListener("change", (e) => setPrefersDark(e.matches));
    }, []);
    const t = useMemo(() => {
        var _a;
        if (theme === "adaptive") {
            // TODO Use client theme
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const configTheme = (_a = window.AdaptiveUi) === null || _a === void 0 ? void 0 : _a.THEME;
            switch (configTheme) {
                case "kv":
                    return kv;
                case "tkrog":
                    return tkrog;
                case "adaptive3":
                    return adaptive3;
                default:
                    return av;
            }
        }
        if (theme === "admin") {
            return forcedMode === "dark" ? adminDark : admin;
        }
        return theme;
    }, [theme, forcedMode]);
    useEffect(() => {
        if (local)
            return;
        document.body.classList.add("aui-root");
        document.body.classList.add("aui-theme-" + t.name);
        return () => {
            document.body.classList.remove("aui-root");
            document.body.classList.remove("aui-theme-" + t.name);
        };
    }, [local, t]);
    const scope = `aui-root aui-theme-${t.name}`;
    return (React.createElement(AdaptiveUiContext.Provider, { value: {
            scope,
            icons: t.icons,
            themeMode: forcedMode,
            setThemeMode: setUserMode,
        } },
        React.createElement(AdaptiveUiThemeStyle, { theme: t, selector: local ? `.aui-root.aui-theme-${t.name}` : ":root" }),
        local ? React.createElement("div", { className: scope }, children) : children));
}
